import type { Currency, ExchangeRatesResponse } from '@/types';
import { currencyApi } from '@/lib/api';
import { useOptimizedQuery } from './useOptimizedQuery';

/**
 * Hook to fetch current exchange rates for a given base currency
 * @param baseCurrency - The base currency (default: 'ISK')
 */
export function useCurrencyRates(baseCurrency: Currency = 'ISK') {
  return useOptimizedQuery<ExchangeRatesResponse>({
    queryKey: ['currency-rates', baseCurrency],
    queryFn: () => currencyApi.getRates(baseCurrency),
    tier: 'realtime', // 5-minute cache for frequently changing data
  });
}

/**
 * Hook to convert amount from one currency to another
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency
 */
export function useCurrencyConversion(
  amount: number,
  fromCurrency: Currency = 'ISK',
  toCurrency: Currency = 'USD'
) {
  const ratesQuery = useCurrencyRates(fromCurrency);

  const convertedAmount = ratesQuery.data
    ? amount * (ratesQuery.data.rates[toCurrency] || 0)
    : null;

  return {
    convertedAmount,
    isLoading: ratesQuery.isLoading,
    isError: ratesQuery.isError,
    error: ratesQuery.error,
  };
}
